/**
 * PDF 生成 API クライアント。
 *
 * BE 仕様: `POST /api/pdf/exports` の単一エンドポイントから purpose × output_method の
 * 組合せで複数の PDF 種別を扱う（[docs/api/pdf-exports.md] 参照）。
 *
 * `output_method: "direct_download"` の場合は同期で 200 OK + PDF バイナリが返り、
 * `Content-Disposition: attachment; filename*=UTF-8''<encoded>` ヘッダで
 * BE 提供のファイル名（例: `契約書_<code_name>_<yymmdd>.pdf`）が返る。
 *
 * バイナリレスポンス × ワンショット呼び出し × キャッシュ不要のため、
 * RTK Query は使わず fetch ベースの薄いラッパーとして実装する。
 *
 * 認証ヘッダ・CSRF トークンは [src/common/utils.ts] のヘルパを再利用し、
 * baseURL は [src/constant/host.ts] の `HOSTNAME` を参照する。
 */
import {
  getAuthTokenFromCookies,
  getCsrfTokenFromCookies,
} from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

export type PdfExportPurpose =
  | "staff_credit_pdf"
  | "offer_subcontract_pdf"
  | "step_estimate_pdf";

/**
 * PDF 生成 API のエラー。
 *
 * - `status`: HTTP ステータスコード。0 はネットワーク失敗（fetch 例外）等。
 * - `beMessage`: BE が JSON で返した `message` フィールド。
 * - `fieldErrors`: BE が JSON で返した `errors` フィールド（フィールド単位のバリデーションエラー）。
 */
export class PdfExportError extends Error {
  status: number;
  beMessage?: string;
  fieldErrors?: Record<string, string[] | string>;

  constructor(
    status: number,
    message: string,
    beMessage?: string,
    fieldErrors?: Record<string, string[] | string>,
  ) {
    super(message);
    this.name = "PdfExportError";
    this.status = status;
    this.beMessage = beMessage;
    this.fieldErrors = fieldErrors;
  }
}

interface ErrorJsonShape {
  message?: string;
  errors?: Record<string, string[] | string>;
  [k: string]: unknown;
}

/** エラー時のレスポンス本文をできる範囲で読んで {message, errors} に変換する */
const readErrorBody = async (
  res: Response,
): Promise<{ beMessage?: string; fieldErrors?: Record<string, string[] | string> }> => {
  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/json")) {
    return {};
  }
  try {
    const body = (await res.json()) as ErrorJsonShape;
    return {
      beMessage: typeof body.message === "string" ? body.message : undefined,
      fieldErrors: body.errors,
    };
  } catch {
    return {};
  }
};

/**
 * `Content-Disposition` ヘッダからファイル名を抽出する。
 *
 * RFC 5987 形式（`filename*=UTF-8''<encoded>`）を優先し、
 * 該当がなければ ASCII 形式（`filename="..."`）を見る。
 *
 * @returns ファイル名。取得できなければ `null`。
 */
export const parseContentDispositionFilename = (
  headers: Headers,
): string | null => {
  const value = headers.get("content-disposition");
  if (!value) return null;

  // RFC 5987: filename*=UTF-8''<percent-encoded>
  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(value);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""));
    } catch {
      /* 続けて ASCII 形式へフォールバック */
    }
  }

  // 旧形式: filename="..."  もしくは filename=...
  const asciiMatch = /filename\s*=\s*("([^"]+)"|([^;]+))/i.exec(value);
  const ascii = asciiMatch?.[2] ?? asciiMatch?.[3];
  if (ascii) return ascii.trim();

  return null;
};

interface DirectDownloadBody {
  purpose: PdfExportPurpose;
  output_method: "direct_download";
  // 値は purpose ごとに自由（step_estimate_pdf は明細行の配列を含む）
  params: Record<string, unknown>;
}

/**
 * direct_download の共通呼び出し。purpose / params の差し替えのみで再利用する。
 *
 * @returns `{ blob, filename }`。`filename` は `Content-Disposition` を優先、
 *          取れなければ `null`（呼び出し側でフォールバックすること）。
 * @throws PdfExportError ネットワーク失敗・非 2xx・想定外レスポンス時
 */
const runPdfExportDirectDownload = async (
  body: DirectDownloadBody,
): Promise<{ blob: Blob; filename: string | null }> => {
  const token = getAuthTokenFromCookies();
  const csrfToken = getCsrfTokenFromCookies();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/pdf, application/json",
  };
  if (token) headers["Authorization"] = `Token ${token}`;
  if (csrfToken) headers["X-CSRFToken"] = csrfToken;

  let res: Response;
  try {
    res = await fetch(`${HOSTNAME}/api/pdf/exports`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new PdfExportError(
      0,
      e instanceof Error ? e.message : "ネットワークエラーが発生しました。",
    );
  }

  if (!res.ok) {
    const { beMessage, fieldErrors } = await readErrorBody(res);
    throw new PdfExportError(
      res.status,
      beMessage || `PDF 生成に失敗しました (HTTP ${res.status})`,
      beMessage,
      fieldErrors,
    );
  }

  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/pdf")) {
    throw new PdfExportError(
      res.status,
      `サーバーから PDF 以外のレスポンスが返されました (Content-Type: ${contentType || "不明"})。`,
    );
  }

  const blob = await res.blob();
  const filename = parseContentDispositionFilename(res.headers);
  return { blob, filename };
};

/**
 * purpose=`staff_credit_pdf` × output_method=`direct_download`。
 * プロジェクトのスタッフクレジット PDF を返す。
 */
export async function requestStaffCreditPdf(args: {
  projectId: string;
  language?: "jp" | "en";
}): Promise<{ blob: Blob; filename: string | null }> {
  const { projectId, language } = args;
  return runPdfExportDirectDownload({
    purpose: "staff_credit_pdf",
    output_method: "direct_download",
    params: {
      project_id: projectId,
      ...(language ? { language } : {}),
    },
  });
}

/**
 * purpose=`offer_subcontract_pdf` × output_method=`direct_download`。
 * 再委託オファー契約 PDF を返す。
 *
 * `dmId` は DM スレッドの ID（= `OfferProject.offer_id` UUID = `DmThread.id`）。
 * BE 側で subcontract 種別のみ受け付け、master_contract の dmId は 400 を返す。
 */
export async function requestOfferSubcontractPdf(args: {
  dmId: string;
  language?: "jp" | "en";
}): Promise<{ blob: Blob; filename: string | null }> {
  const { dmId, language } = args;
  return runPdfExportDirectDownload({
    purpose: "offer_subcontract_pdf",
    output_method: "direct_download",
    params: {
      dm_id: dmId,
      ...(language ? { language } : {}),
    },
  });
}

/** ステップ見積もり PDF の明細 1 行（BE の params.lines と対応）。 */
export interface StepEstimatePdfLine {
  /** 内容欄（項目名）。 */
  name: string;
  /** 数値単価 or 表示文字列（"一式" 等。文字列は金額計算外）。 */
  price: number | string;
  quantity: number;
  unit: string;
  note: string;
}

/**
 * purpose=`step_estimate_pdf` × output_method=`direct_download`。
 * ステップ見積もり -超松竹梅- の確定明細から御見積書 PDF を返す。
 *
 * 明細は FE（ツール）側で確定済みのものを params 経由で渡し、
 * BE は Vivliostyle の quotation テンプレート互換 payload に変換する。
 * 合計・消費税は BE 側で再計算される。
 */
export async function requestStepEstimatePdf(args: {
  /** 見積もりの件名（任意）。 */
  subject?: string;
  /** 宛名（「◯◯ 御中」の ◯◯ 部分、任意）。 */
  customerName?: string;
  /**
   * 発行者欄（任意）。省略した項目はログインユーザーの登録内容が使われる。
   * ロゴ・社印・サインは常にマイページの設定から取る。
   */
  issuer?: {
    companyName?: string;
    jobTitle?: string;
    addressLine1?: string;
    addressLine2?: string;
  };
  lines: StepEstimatePdfLine[];
}): Promise<{ blob: Blob; filename: string | null }> {
  const { subject, customerName, issuer, lines } = args;
  return runPdfExportDirectDownload({
    purpose: "step_estimate_pdf",
    output_method: "direct_download",
    params: {
      lines,
      ...(subject ? { subject } : {}),
      ...(customerName ? { customer_name: customerName } : {}),
      ...(issuer?.companyName ? { issuer_company_name: issuer.companyName } : {}),
      ...(issuer?.jobTitle ? { issuer_job_title: issuer.jobTitle } : {}),
      ...(issuer?.addressLine1
        ? { issuer_address_line1: issuer.addressLine1 }
        : {}),
      ...(issuer?.addressLine2
        ? { issuer_address_line2: issuer.addressLine2 }
        : {}),
    },
  });
}

/**
 * `PdfExportError` をユーザー向けの日本語メッセージに整形する。
 *
 * 401/403/404/400/5xx を判別して定型文を返し、未分類は BE message を素通しする。
 * 403 の文言は purpose ごとに権限要件が異なるため、`forbiddenMessage` で
 * 上書き可能にしている。
 */
export const formatPdfExportError = (
  err: unknown,
  options?: { forbiddenMessage?: string },
): string => {
  if (err instanceof PdfExportError) {
    if (err.status === 0) {
      return "サーバーに接続できませんでした。ネットワーク／接続先 URL をご確認ください。";
    }
    if (err.status === 401) {
      return "認証が必要です (401)。再ログインしてください。";
    }
    if (err.status === 403) {
      return (
        options?.forbiddenMessage ||
        "PDF を書き出す権限がありません (403)。プロジェクトの MASTERADMIN/OWNER/PRODUCER 権限が必要です。"
      );
    }
    if (err.status === 404) {
      return "PDF 生成 API が見つかりませんでした (404)。BE 側の実装/デプロイをご確認ください。";
    }
    if (err.status === 400) {
      const fieldText = err.fieldErrors
        ? Object.entries(err.fieldErrors)
            .map(([k, v]) =>
              Array.isArray(v) ? `${k}: ${v.join(", ")}` : `${k}: ${v}`,
            )
            .join(" / ")
        : "";
      return `入力内容にエラーがあります: ${err.beMessage ?? ""} ${fieldText}`.trim();
    }
    if (err.status >= 500) {
      return `サーバーエラーが発生しました (${err.status})。しばらく経ってから再度お試しください。`;
    }
    return err.beMessage || err.message || `PDF 生成に失敗しました (${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return "PDF 生成に失敗しました。";
};
